import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

import { ApiParamsType } from '@/features/shared/types/ApiParamsType';

import { getUsers } from '../actions/getUsers';

type GetUsersParams = ApiParamsType & {
  organizationId?: string;
  role?: string;
  alphaLevel?: string;
};

const getUsersQueryKey = (params: GetUsersParams) => ['users', params];

export const getUsersQueryOptions = (params: GetUsersParams) => {
  let filter = params.filter || "status='active'";
  if (params.organizationId) {
    filter = `${filter} AND primaryOrg='${params.organizationId}'`;
  }

  if (params.role) {
    filter = `${filter} AND roles.role='${params.role}'`;
  }

  if (params.alphaLevel) {
    filter = `${filter} AND metadata.alphaLevel='${params.alphaLevel}'`;
  }

  const cleanParams = {
    ...params,
    filter,
  };

  return queryOptions({
    queryKey: getUsersQueryKey(cleanParams),
    staleTime: 0, // always fetch fresh data

    placeholderData: keepPreviousData, // don't have 0 rows flash while changing pages/loading next page
    queryFn: async () => {
      const response = await getUsers({
        data: cleanParams,
      });

      if (!response.success || response.error) {
        throw new Error(response.error || 'Unknown error');
      }

      return response.data;
    },
  });
};

export const useGetUsers = (params: GetUsersParams) =>
  useQuery(
    getUsersQueryOptions({
      filter: params.filter,
      sort: params.sort,
      orderBy: params.orderBy,
      limit: params.limit,
      offset: params.offset,
      search: params.search,
      organizationId: params.organizationId,
      role: params.role,
      alphaLevel: params.alphaLevel,
    })
  );
